import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import OneProject from "./OneProject";
import { FaArrowRight } from "react-icons/fa6";

/* ------------------------------------------------------------------ */
/* Scroll-reveal utility                                              */
/* ------------------------------------------------------------------ */
function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px", ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ as: Tag = "div", children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */
export default function ProjectsBanner(props) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  async function init() {
    setReady(false);
    await Promise.all([
      appContext.getAllProjects(),
    ]);
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  if (!ready) {
    return (
      <div className="container py-5 d-flex justify-content-center" style={{ backgroundColor: '#0f172a', minHeight: '50vh', alignItems: 'center' }}>
        {getInlineLoader()}
      </div>
    );
  }

  const allProjects = Array.isArray(appContext.allProjects) ? appContext.allProjects : [];
  
  // Routing Logic Check (Using 'projects' route string)
  const isProjectsRoute = appContext.item === 'projects';
  const displayProjects = isProjectsRoute ? allProjects : allProjects.slice(0, 3);

  return (
    <section className="mod-projects-section">
      <style>{`
        /* --- Base & Dark Theme --- */
        .mod-projects-section {
          background-color: #0f172a; /* Deep Navy Slate */
          color: #f8fafc;
          font-family: system-ui, -apple-system, sans-serif;
          padding: 6rem 0;
          position: relative;
          min-height: 50vh;
        }

        /* --- Scroll Reveal Animations --- */
        .reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        .reveal.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* --- Typography --- */
        .mod-kicker {
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #60a5fa;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: block;
        }
        .mod-title {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 3rem;
          line-height: 1.2;
        }

        /* --- Dynamic Project Wrapping for dark theme --- */
        .project-dynamic-wrap {
          height: 100%;
          transition: transform 0.3s ease;
        }
        .project-dynamic-wrap:hover {
          transform: translateY(-4px);
        }
        /* Forcing dark mode properties on internal OneProject components if they use standard bootstrap styling */
        .project-dynamic-wrap .card, 
        .project-dynamic-wrap .bg-white,
        .project-dynamic-wrap .bg-light {
          background: rgba(30, 41, 59, 0.5) !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          color: #f8fafc !important;
          backdrop-filter: blur(10px);
          border-radius: 12px;
          overflow: hidden;
        }
        .project-dynamic-wrap .text-dark, 
        .project-dynamic-wrap .text-muted {
          color: #94a3b8 !important;
        }

        .mod-empty-state {
          background: rgba(30, 41, 59, 0.3);
          border: 1px dashed rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 3rem;
          color: #64748b;
        }

        /* --- Buttons --- */
        .mod-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 32px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          border: none;
        }
        .mod-btn-outline {
          background-color: transparent;
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(5px);
        }
        .mod-btn-outline:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.8);
          transform: translateY(-2px);
        }
      `}</style>

      <div className="container">
        
        {/* --- SECTION HEADER --- */}
        <div className="text-center mb-5">
          <Reveal>
            <span className="mod-kicker">Portfolio</span>
            <h2 className="mod-title mb-2">Our Projects</h2>
            <p style={{ color: '#94a3b8', maxWidth: '600px', margin: '0 auto' }}>
              A track record of successful deliveries, complex logistics operations, and large-scale corporate supply contracts.
            </p>
          </Reveal>
        </div>

        {/* --- PROJECTS GRID --- */}
        <div className="row g-4 justify-content-center">
          {displayProjects.length > 0 ? (
            displayProjects.map((item, i) => (
              <div key={i} className="col-12 col-md-6 col-lg-4">
                <Reveal delay={i * 100} className="project-dynamic-wrap">
                  <OneProject data={item} />
                </Reveal>
              </div>
            ))
          ) : (
            <div className="col-12">
              <Reveal className="mod-empty-state text-center">
                <p className="mb-0 fst-italic">No projects are available at this time.</p>
              </Reveal>
            </div>
          )}
        </div>

        {/* --- CONDITIONAL CTA: VIEW ALL PROJECTS --- */}
        {!isProjectsRoute && allProjects.length > 3 && (
          <Reveal delay={200}>
            <div className="text-center mt-5 pt-3">
              <button
                className="mod-btn mod-btn-outline"
                onClick={() => appContext.navTo({ item: "projects" })}
              >
                View All Projects <FaArrowRight />
              </button>
            </div>
          </Reveal>
        )}

      </div>
    </section>
  );
}